import { subclass, property } from "@arcgis/core/core/accessorSupport/decorators";

import Widget from "@arcgis/core/widgets/Widget";

import { messageBundle, storeNode, tsx } from "@arcgis/core/widgets/support/widget";

import "./Sidebar/Sidebar.scss";
import NavBarViewModel from '../ui/NavBar/NavBarViewModel';
import AppT9N from "../../t9n/App/App.json";
import { EAppTemplateType } from 'templates-common-library/baseClasses/CompatibilityChecker';
import { VNode } from 'src/interfaces/interfaces';
import { isRTL, keyboardAccessibleHelper } from '../utils/appUtils';

@subclass("Landing.widgets.Sidebar")
export default class Sidebar extends Widget {

  //--------------------------------------------------------------------------
  //
  //  Properties
  //
  //--------------------------------------------------------------------------

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof AppT9N;

  @property()
  navbarVM: NavBarViewModel;

  @property()
  filteredTemplateList: EAppTemplateType[];

  @property()
  firstAnswerTemplates: EAppTemplateType[];

  @property()
  secondAnswerTemplates: EAppTemplateType[];

  @property()
  thirdAnswerTemplates: EAppTemplateType[];

  _secondQuestion: QuestionInfo;
  _thirdQuestion: QuestionInfo;

  _firstQuestionDOM: any;
  _secondQuestionDOM: any;
  _thirdQuestionDOM: any;

  _selectionAnswersState: {
    "0"?: number, // answerIndex number
    "1"?: number, // answerIndex number
    "2"?: number  // answerIndex number
  } = {};

  _currentFocusDepth: number = null;
  _shouldShowTerminalMsg: boolean = false;

  sidebarCloseBtn: HTMLCalciteIconElement;

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  postInitialize() {
    this.watch(["firstAnswerTemplates", "secondAnswerTemplates", "thirdAnswerTemplates"], () => {
      this._calcFilterList();
    });

    this.navbarVM.watch(["suggestionsButtonSignal", "navigationRoute"], () => {
      this._calcFilterList();
    });

    this.navbarVM.watch(["suggestionsButtonSignal"], () => {
      if (this.navbarVM.suggestionsButtonSignal === true) {
        setTimeout(() => {
          this.sidebarCloseBtn?.focus();
        }, 250);
      }
    });
  }

  render() {
    return (
      <div key="sidebar-area" id="sidebar-area"
        class={
          this.classes(
            "sidebar-area",
            this.navbarVM.suggestionsButtonSignal ? "sidebar-area-expanded" : ""
          )
        }>
        {
          this.navbarVM.suggestionsButtonSignal
            ? [
              this._renderTitle(),
              this.navbarVM.itemLoaderVM.loadedItemType === "group"
                ? this._renderGroupResourceType()
                : this.navbarVM.navigationRoute === "browseAll"
                  ? this._renderOptions()
                  : this._renderGoToBrowseAll()
            ]
            : null
        }
      </div>
    );
  }

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------


  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private _renderGoToBrowseAll(): VNode {
    const isRTLVal: boolean = isRTL();
    const { goToBrowseAll1, goToBrowseAll2 } = this.appMessages.suggestions;
    return (
      <div key="go-to-browseAll" class="go-to-browseAll">
        <div>
          {goToBrowseAll1}
        </div>
        <br />
        <calcite-button
          icon-start={isRTLVal ? "arrow-right" : "arrow-left"}
          onclick={() => { this.navbarVM.setNavbarRoute("browseAll") }}>
          {goToBrowseAll2}
        </calcite-button>
      </div>
    )
  }

  private _renderGroupResourceType(): VNode {
    const { groupResource } = this.appMessages.suggestions;
    return (
      <div key="groupResource" class="go-to-browseAll">
        <div>
          {groupResource}
        </div>
      </div>
    )
  }


  private _calcFilterList(): void {
    if (this.navbarVM.suggestionsButtonSignal === false || this.navbarVM.navigationRoute !== "browseAll") {
      this.filteredTemplateList = null;
    } else {
      this.filteredTemplateList = this.thirdAnswerTemplates
        || this.secondAnswerTemplates
        || this.firstAnswerTemplates;
    }
  }

  private _renderTitle() {
    const { suggestions, desc, tip } = this.appMessages.suggestions;
    const toggleVis = () => { this.navbarVM.toggleSuggBtnSignal() };
    return (
      <div id="sugg-title"
        class={this.classes(
          this.navbarVM.suggestionsButtonSignal ? "sidebar-area-expanded" : ""
        )}>
        <div>
          <h2>{suggestions}</h2>
          <p>
            {desc}
            &nbsp;
            <calcite-tooltip-manager>
              <calcite-icon scale="s" tabindex="0" icon="information" class="info-icon" id="info-icon"></calcite-icon>
              <calcite-tooltip label={tip} placement="bottom" reference-element="info-icon">
                {this.appMessages.suggestions["2ndTip"]}
              </calcite-tooltip>
            </calcite-tooltip-manager>
          </p>
        </div>

        <div class="x-btn">
          <calcite-icon
            onclick={toggleVis}
            onkeyup={(e) => { keyboardAccessibleHelper(toggleVis, e) }}
            icon="x"
            tabindex="0"
            afterCreate={storeNode} bind={this} data-node-ref="sidebarCloseBtn"
            scale="l"
          ></calcite-icon>
        </div>
      </div>
    )
  }

  private _renderOptions() {
    const rootQuestion: QuestionInfo = this._decideRootQuestion();
    this._firstQuestionDOM = this._renderQuestion(rootQuestion, 0);
    this._secondQuestionDOM = this._renderQuestion(this._secondQuestion, 1);
    this._thirdQuestionDOM = this._renderQuestion(this._thirdQuestion, 2);
    return (
      <div
        key="questions-section"
        class={this.classes(
          "questions-section",
          this.navbarVM.suggestionsButtonSignal ? "sidebar-area-expanded" : ""
        )}>
        { this._firstQuestionDOM}
        { this._secondQuestionDOM}
        { this._thirdQuestionDOM}
        { this._shouldShowTerminalMsg ? this._renderTerminalMsg() : null}
      </div>
    )
  }

  private _renderTerminalMsg(): VNode {
    const { terminalResponse } = this.appMessages.suggestions;
    return (
      <div class="terminal-message">
        <calcite-icon
          scale="l"
          class="terminal-icon"
          icon="check-circle-f"
        ></calcite-icon>
        { terminalResponse}
      </div>
    )
  }

  /** All the same RootQuestion for now, but could change in the future */
  private _decideRootQuestion(): QuestionInfo {
    const { loadedItemType } = this.navbarVM.itemLoaderVM;
    if (loadedItemType === "none") {
      return this._constructQuestionTree_NoResource();
    } else if (loadedItemType === "webmap") {
      return this._constructQuestionTree_NoResource();
    } else if (loadedItemType === "webscene") {
      return this._constructQuestionTree_NoResource();
    }
  }

  private _sendFocusToQuestionHelper(questionVNodeRef: string) {
    if (questionVNodeRef === null) { return; }
    setTimeout(() => {
      this?.[questionVNodeRef]?.children?.[1]?.children?.[0]?.domNode?.focus();
    }, 400)
  }

  private _clickOnAnswer(answerInfo: AnswerInfo, depth: number, answerIndex: number) {
    const { nextQuestion, resultingApps } = answerInfo;
    this._currentFocusDepth = null;
    if (depth === 0) {
      this.resetDepthTo0();
      this._secondQuestion = nextQuestion;
      this.firstAnswerTemplates = resultingApps;
      this._sendFocusToQuestionHelper("_secondQuestionDOM");
    } else if (depth === 1) {
      this.resetDepthTo1();
      this._thirdQuestion = nextQuestion;
      this.secondAnswerTemplates = resultingApps;
      this._sendFocusToQuestionHelper("_thirdQuestionDOM");
    } else if (depth === 2) {
      this.thirdAnswerTemplates = resultingApps;
    }

    this._selectionAnswersState[depth] = answerIndex;

    this._shouldShowTerminalMsg = nextQuestion == null;
  }

  resetDepthTo0() {
    this._secondQuestion = null;
    this._thirdQuestion = null;

    this.firstAnswerTemplates = null;
    this.secondAnswerTemplates = null;
    this.thirdAnswerTemplates = null;

    this._selectionAnswersState[0] = null;
    this._selectionAnswersState[1] = null;
    this._selectionAnswersState[2] = null;
  }

  resetDepthTo1() {
    this._thirdQuestion = null;

    this.secondAnswerTemplates = null;
    this.thirdAnswerTemplates = null;

    this._selectionAnswersState[1] = null;
    this._selectionAnswersState[2] = null;
  }

  resetDepthTo2() {
    this.thirdAnswerTemplates = null;

    this._selectionAnswersState[2] = null;
  }

  private _renderQuestion(question: QuestionInfo, depth: number): VNode {
    if (question == null) {
      return null;
    }

    const currAnswerState: number = this._selectionAnswersState[depth];
    if (currAnswerState == null || depth === this._currentFocusDepth) {
      return this._renderActiveQuestion(question, depth);
    } else {
      return this._renderAnsweredQuestion(question, depth);
    }

  }

  _clearAction = (depth: number) => {
    this._shouldShowTerminalMsg = false;
    switch (depth) {
      case 0:
        this.resetDepthTo0();
        this._sendFocusToQuestionHelper("_firstQuestionDOM");
        break;
      case 1:
        this.resetDepthTo1();
        this._sendFocusToQuestionHelper("_secondQuestionDOM");
        break;
      case 2:
        this.resetDepthTo2();
        this._sendFocusToQuestionHelper("_thirdQuestionDOM");
    }
  };

  private _renderActiveQuestion(question: QuestionInfo, depth: number) {
    const formattedKey = question.questionText.split(" ").join("");
    const questionNumber = depth + 1;

    return (
      <div key={formattedKey} class="question-tree">
        <div class="question">

          <span>
            {questionNumber}. {question.questionText}
          </span>
          {this._selectionAnswersState[depth] != null ? <calcite-icon
            scale="m"
            class="clear-icon"
            icon="x"
            tabindex="0"
            onclick={this._clearAction.bind(this, depth)}
            onkeyup={(e) => { keyboardAccessibleHelper(this._clearAction.bind(this, depth), e) }}
          ></calcite-icon> : null}

        </div>
        <div class="answers">
          {
            question.answers.map((answerInfo: AnswerInfo, index: number) => {
              const selectAction = this._clickOnAnswer.bind(this, answerInfo, depth, index);

              return (
                <div class="answer" key={`${formattedKey}-answer-${index}`}
                  tabindex="0"
                  onclick={selectAction}
                  onkeyup={(e) => { keyboardAccessibleHelper(selectAction, e) }}
                >
                  <calcite-icon
                    scale="m"
                    class="answer-icon"
                    icon={
                      this._selectionAnswersState[depth] === index
                        ? "check"
                        : "bullet-point"}
                  ></calcite-icon>
                  &nbsp;
                  <div>
                    {answerInfo.answerText}
                  </div>
                </div>
              )
            })
          }
        </div>
      </div>
    )
  }

  private _renderAnsweredQuestion(question: QuestionInfo, depth: number) {
    const formattedKey = question.questionText.split(" ").join("");
    const questionNumber = depth + 1;
    return (
      <div key={`${formattedKey}-ANSWERED`} class="answered-question">
        <div class="question">
          <span>
            {questionNumber}. {question.questionText}
          </span>
          <calcite-icon
            scale="m"
            class="clear-icon"
            icon="x"
            tabindex="0"
            onclick={this._clearAction.bind(this, depth)}
            onkeyup={(e) => { keyboardAccessibleHelper(this._clearAction.bind(this, depth), e) }}
          ></calcite-icon>
        </div>
        <div class="answers">
          {
            question.answers.map((answerInfo: AnswerInfo, index: number) => {

              const chgFocusDepth = (depth: number) => {
                this._currentFocusDepth = depth;
                this._sendFocusToQuestionHelper(
                  this._currentFocusDepth === 0
                    ? "_firstQuestionDOM"
                    : this._currentFocusDepth === 1
                      ? "_secondQuestionDOM"
                      : this._currentFocusDepth === 2
                        ? "_thirdQuestionDOM"
                        : null
                )
              }
              return this._selectionAnswersState[depth] === index ?
                (
                  <div
                    class="answer"
                    key={`${formattedKey}-picked-answer`}
                    onclick={() => { chgFocusDepth.call(this, depth) }}
                    onkeyup={(e) => { keyboardAccessibleHelper(chgFocusDepth.bind(this, depth), e) }}
                    tabindex="0"
                  >
                    <div class="answer-text">
                      <calcite-icon
                        scale="m"
                        class="answer-icon"
                        icon="check"
                      ></calcite-icon>

                      <div>
                        {answerInfo.answerText}
                      </div>

                    </div>

                    <calcite-icon
                      scale="m"
                      class="reanswer-icon"
                      icon="chevron-up"
                    ></calcite-icon>
                  </div>
                ) : null
            }
            )
          }
        </div>
      </div>
    )
  }

  private _constructQuestionTree_NoResource(): QuestionInfo {
    const {
      Q_What_is_your_goal_for_the_app, A_Showcase_one_or_more_maps_with_essential_tools, A_Allow_viewers_to_interact_with_your_data, A_Provide_public_information, A_Display_a_variety_of_features_and_content_types,
      Q_What_do_you_want_to_display_in_the_app, A_One_web_map, A_One_web_scene, A_Multiple_maps_and_scenes,
      Q_Does_the_map_include_an_image_service, A_Yes, A_No,

      Q_What_do_you_want_viewers_to_do_with_your_data, A_Measure_distance, A_Compare_maps_and_data, A_Filter_features, A_Edit_data, A_Explore_graphic_and_statistical_information,
      Q_What_type_of_data_do_you_have, A_Web_Scene, A_Web_Map, A_Map_with_Image_Service,
      Q_What_do_you_want_to_compare, A_Maps, A_Layers, A_Features,
      Q_Does_your_map_include_time_enabled_data,
      Q_What_do_you_want_to_display, A_Charts_saved_in_a_map, A_Ranked_lists_of_features, A_Time_animation,
      Q_What_do_you_want_to_share, A_Images_stored_as_feature_attachments, A_Graphic_and_statistical_information,
      A_Proximity_to_a_feature, A_ArcGIS_items_and_other_resources,
      Q_What_is_the_feature_geometry_type, A_Points_or_lines, A_Polygons,
      Q_Is_the_content_shared_to_a_group, A_Bookmarked_locations
    } = this.appMessages.suggestions.qaCollection;
    return {
      questionText: Q_What_is_your_goal_for_the_app,
      answers: [
        {
          answerText: A_Showcase_one_or_more_maps_with_essential_tools,
          resultingApps: [
            EAppTemplateType.ThreeDViewer,
            EAppTemplateType.Basic,
            EAppTemplateType.Exhibit,
            EAppTemplateType.ImageryApp,
            EAppTemplateType.Media,
            EAppTemplateType.Minimalist,
            EAppTemplateType.Portfolio
          ],
          nextQuestion: {
            questionText: Q_What_do_you_want_to_display_in_the_app,
            answers: [
              {
                answerText: A_One_web_map,
                resultingApps: [
                  EAppTemplateType.Basic,
                  EAppTemplateType.Exhibit,
                  EAppTemplateType.ImageryApp,
                  EAppTemplateType.Media,
                  EAppTemplateType.Minimalist
                ],
                nextQuestion: {
                  questionText: Q_Does_the_map_include_an_image_service,
                  answers: [
                    {
                      answerText: A_Yes,
                      resultingApps: [EAppTemplateType.ImageryApp],
                      nextQuestion: null
                    },
                    {
                      answerText: A_No,
                      resultingApps: [
                        EAppTemplateType.Basic,
                        EAppTemplateType.Exhibit,
                        EAppTemplateType.Media,
                        EAppTemplateType.Minimalist
                      ],
                      nextQuestion: null
                    }
                  ]
                }
              },
              {
                answerText: A_One_web_scene,
                resultingApps: [
                  EAppTemplateType.ThreeDViewer,
                  EAppTemplateType.Basic
                ],
                nextQuestion: null
              },
              {
                answerText: A_Multiple_maps_and_scenes,
                resultingApps: [
                  EAppTemplateType.Portfolio
                ],
                nextQuestion: null
              }
            ]
          }
        },
        {
          answerText: A_Allow_viewers_to_interact_with_your_data,
          resultingApps: [
            EAppTemplateType.ThreeDViewer,
            EAppTemplateType.Charts,
            EAppTemplateType.Countdown,
            EAppTemplateType.ImageryApp,
            EAppTemplateType.InteractiveLegend,
            EAppTemplateType.Media,
            EAppTemplateType.Nearby,
            EAppTemplateType.Portfolio,
            EAppTemplateType.Sidebar,
            EAppTemplateType.Slider
          ],
          nextQuestion: {
            questionText: Q_What_do_you_want_viewers_to_do_with_your_data,
            answers: [
              {
                answerText: A_Measure_distance,
                resultingApps: [
                  EAppTemplateType.ThreeDViewer,
                  EAppTemplateType.ImageryApp,
                  EAppTemplateType.Media
                ],
                nextQuestion: {
                  questionText: Q_What_type_of_data_do_you_have,
                  answers: [
                    {
                      answerText: A_Web_Scene,
                      resultingApps: [EAppTemplateType.ThreeDViewer],
                      nextQuestion: null
                    },
                    {
                      answerText: A_Web_Map,
                      resultingApps: [EAppTemplateType.Media],
                      nextQuestion: null
                    },
                    {
                      answerText: A_Map_with_Image_Service,
                      resultingApps: [EAppTemplateType.ImageryApp],
                      nextQuestion: null
                    }
                  ]
                }
              },
              {
                answerText: A_Compare_maps_and_data,
                resultingApps: [
                  EAppTemplateType.Countdown,
                  EAppTemplateType.ImageryApp,
                  EAppTemplateType.Media,
                  EAppTemplateType.Portfolio
                ],
                nextQuestion: {
                  questionText: Q_What_do_you_want_to_compare,
                  answers: [
                    {
                      answerText: A_Maps,
                      resultingApps: [EAppTemplateType.Portfolio],
                      nextQuestion: null
                    },
                    {
                      answerText: A_Features,
                      resultingApps: [EAppTemplateType.Countdown],
                      nextQuestion: null
                    },
                    {
                      answerText: A_Layers,
                      resultingApps: [
                        EAppTemplateType.ImageryApp,
                        EAppTemplateType.Media
                      ],
                      nextQuestion: null
                    }
                  ]
                }
              },
              {
                answerText: A_Filter_features,
                resultingApps: [
                  EAppTemplateType.InteractiveLegend,
                  EAppTemplateType.Media,
                  EAppTemplateType.Nearby,
                  EAppTemplateType.Sidebar,
                  EAppTemplateType.Slider
                ],
                nextQuestion: {
                  questionText: Q_Does_your_map_include_time_enabled_data,
                  answers: [
                    {
                      answerText: A_Yes,
                      resultingApps: [
                        EAppTemplateType.InteractiveLegend,
                        EAppTemplateType.Media,
                        EAppTemplateType.Slider
                      ],
                      nextQuestion: null
                    },
                    {
                      answerText: A_No,
                      resultingApps: [
                        EAppTemplateType.Nearby,
                        EAppTemplateType.Sidebar
                      ],
                      nextQuestion: null
                    }
                  ]
                }
              },
              {
                answerText: A_Edit_data,
                resultingApps: [
                  EAppTemplateType.AttachmentViewer,
                  EAppTemplateType.ImageryApp,
                  EAppTemplateType.Sidebar
                ],
                nextQuestion: null
              },
              {
                answerText: A_Explore_graphic_and_statistical_information,
                resultingApps: [
                  EAppTemplateType.Charts,
                  EAppTemplateType.Countdown,
                  EAppTemplateType.InteractiveLegend,
                  EAppTemplateType.Slider
                ],
                nextQuestion: {
                  questionText: Q_What_do_you_want_to_display,
                  answers: [
                    {
                      answerText: A_Charts_saved_in_a_map,
                      resultingApps: [EAppTemplateType.Charts],
                      nextQuestion: null
                    },
                    {
                      answerText: A_Ranked_lists_of_features,
                      resultingApps: [EAppTemplateType.Countdown],
                      nextQuestion: null
                    },
                    {
                      answerText: A_Time_animation,
                      resultingApps: [
                        EAppTemplateType.InteractiveLegend,
                        EAppTemplateType.Slider
                      ],
                      nextQuestion: null
                    }
                  ]
                }
              }
            ]
          }
        },
        {
          answerText: A_Provide_public_information,
          resultingApps: [
            EAppTemplateType.AttachmentViewer,
            EAppTemplateType.CategoryGallery,
            EAppTemplateType.Charts,
            EAppTemplateType.Countdown,
            EAppTemplateType.Nearby,
            EAppTemplateType.Portfolio,
            EAppTemplateType.Slider,
            EAppTemplateType.ZoneLookup
          ],
          nextQuestion: {
            questionText: Q_What_do_you_want_to_share,
            answers: [
              {
                answerText: A_Images_stored_as_feature_attachments,
                resultingApps: [EAppTemplateType.AttachmentViewer],
                nextQuestion: null
              },
              {
                answerText: A_Graphic_and_statistical_information,
                resultingApps: [
                  EAppTemplateType.Charts,
                  EAppTemplateType.Countdown,
                  EAppTemplateType.InteractiveLegend,
                  EAppTemplateType.Slider
                ],
                nextQuestion: {
                  questionText: Q_What_do_you_want_to_display,
                  answers: [
                    {
                      answerText: A_Charts_saved_in_a_map,
                      resultingApps: [EAppTemplateType.Charts],
                      nextQuestion: null
                    },
                    {
                      answerText: A_Ranked_lists_of_features,
                      resultingApps: [EAppTemplateType.Countdown],
                      nextQuestion: null
                    },
                    {
                      answerText: A_Time_animation,
                      resultingApps: [
                        EAppTemplateType.InteractiveLegend,
                        EAppTemplateType.Slider
                      ],
                      nextQuestion: null
                    }
                  ]
                }
              },
              {
                answerText: A_Proximity_to_a_feature,
                resultingApps: [
                  EAppTemplateType.Nearby,
                  EAppTemplateType.ZoneLookup
                ],
                nextQuestion: {
                  questionText: Q_What_is_the_feature_geometry_type,
                  answers: [
                    {
                      answerText: A_Points_or_lines,
                      resultingApps: [EAppTemplateType.Nearby],
                      nextQuestion: null
                    },
                    {
                      answerText: A_Polygons,
                      resultingApps: [EAppTemplateType.ZoneLookup],
                      nextQuestion: null
                    }
                  ]
                }
              },
              {
                answerText: A_ArcGIS_items_and_other_resources,
                resultingApps: [
                  EAppTemplateType.CategoryGallery,
                  EAppTemplateType.Portfolio
                ],
                nextQuestion: {
                  questionText: Q_Is_the_content_shared_to_a_group,
                  answers: [
                    {
                      answerText: A_Yes,
                      resultingApps: [EAppTemplateType.CategoryGallery],
                      nextQuestion: null
                    },
                    {
                      answerText: A_No,
                      resultingApps: [EAppTemplateType.Portfolio],
                      nextQuestion: null
                    }
                  ]
                }
              }
            ]
          }
        },
        {
          answerText: A_Display_a_variety_of_features_and_content_types,
          resultingApps: [
            EAppTemplateType.AttachmentViewer,
            EAppTemplateType.CategoryGallery,
            EAppTemplateType.Charts,
            EAppTemplateType.Exhibit,
            EAppTemplateType.Portfolio
          ],
          nextQuestion: {
            questionText: Q_What_do_you_want_to_display,
            answers: [
              {
                answerText: A_Images_stored_as_feature_attachments,
                resultingApps: [EAppTemplateType.AttachmentViewer],
                nextQuestion: null
              },
              {
                answerText: A_Charts_saved_in_a_map,
                resultingApps: [EAppTemplateType.Charts],
                nextQuestion: null
              },
              {
                answerText: A_Bookmarked_locations,
                resultingApps: [EAppTemplateType.Exhibit],
                nextQuestion: null
              },
              {
                answerText: A_ArcGIS_items_and_other_resources,
                resultingApps: [
                  EAppTemplateType.CategoryGallery,
                  EAppTemplateType.Portfolio
                ],
                nextQuestion: {
                  questionText: Q_Is_the_content_shared_to_a_group,
                  answers: [
                    {
                      answerText: A_Yes,
                      resultingApps: [EAppTemplateType.CategoryGallery],
                      nextQuestion: null
                    },
                    {
                      answerText: A_No,
                      resultingApps: [EAppTemplateType.Portfolio],
                      nextQuestion: null
                    }
                  ]
                }
              }
            ]
          }
        }
      ]
    }
  }

  private _constructQuestionTree_Webmap(): QuestionInfo {
    return null;
  }

  private _constructQuestionTree_Webscene(): QuestionInfo {
    return null;
  }
}

export interface QuestionInfo {
  questionText: string;
  answers: AnswerInfo[];
}

export interface AnswerInfo {
  answerText: string;
  resultingApps: EAppTemplateType[];
  nextQuestion: QuestionInfo | null; // null implies you're at a deadend in the tree
}
